import React, { useState, useEffect } from 'react';
import { useSpring, animated} from '@react-spring/web';
import tw from 'twin.macro';
import axios from 'axios';

const Container = tw.div`bg-gradient-to-r from-blue-500 to-indigo-500 px-10 py-12 rounded-lg border border-gray-300 shadow-2xl flex flex-col items-center`;
const Title = tw.h1`text-4xl font-bold text-white mb-10`; // Reduced from 5xl to 4xl
const Form = tw.form`w-full max-w-screen-md bg-white rounded-3xl shadow-2xl p-8 mb-10 flex flex-col items-center sm:items-start`;
const FormControl = tw.div`flex flex-col mb-6 w-full`;
const Label = tw.label`text-base font-medium text-gray-700 mb-2`; // Changed from lg to base
const Select = tw.select`p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200 w-full`;
const Input = tw.input`p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200 w-full`;
const TimeInput = tw.input`p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200 w-full`;
const ButtonGroup = tw.div`flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mt-8 w-full`;
const ConfigList = tw.div`w-full max-w-screen-md bg-white rounded-3xl shadow-2xl p-4 mb-10`;
const ConfigItem = tw.div`p-4 border-b border-gray-200 flex justify-between items-center`;
const ConfigText = tw.span`text-base text-gray-700`; // Changed from lg to base
const ActionButtons = tw.div`flex space-x-4`;
const DeleteButton = tw.button`bg-red-600 text-white px-6 py-2 rounded-xl font-semibold uppercase tracking-wider transition duration-300 transform hover:bg-red-700 hover:scale-105 focus:(outline-none ring-2 ring-red-500 ring-offset-2 ring-offset-blue-500) text-sm`; // Added text-sm and adjusted padding
const Button = tw(animated.button)`bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold uppercase tracking-wider transition duration-300 transform hover:(bg-indigo-900 scale-105) focus:(outline-none ring-2 ring-teal-500 ring-offset-2 ring-offset-blue-500) disabled:(opacity-50 cursor-not-allowed) text-sm`; // Added text-sm and adjusted padding

const StrategyConfiguration = () => {
  const [strategyName, setStrategyName] = useState('');
  const [strategyType, setStrategyType] = useState('');
  const [index, setIndex] = useState('');
  const [minEntryTime, setMinEntryTime] = useState('');
  const [maxEntryTime, setMaxEntryTime] = useState('');
  const [squareOffTime, setSquareOffTime] = useState('');
  const [quantity, setQuantity] = useState('');
  const [bookProfit, setBookProfit] = useState('');
  const [bookLoss, setBookLoss] = useState('');
  const [trailingSlGap, setTrailingSlGap] = useState('');
  const [maxLoss, setMaxLoss] = useState('');
  const [maxEntry, setMaxEntry] = useState('');
  const [configurations, setConfigurations] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const axiosInstance = axios.create({
    baseURL: 'https://falcon-algo.onrender.com/api',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    }
  });


  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const response = await axiosInstance.get('/configurations');
      setConfigurations(response.data);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  };

  const handleAddNew = () => {
    setSelectedConfig(null);
    setStrategyName('');
    setStrategyType('');
    setIndex('');
    setMinEntryTime('');
    setMaxEntryTime('');
    setSquareOffTime('');
    setQuantity('');
    setBookProfit('');
    setBookLoss('');
    setTrailingSlGap('');
    setMaxLoss('');
    setMaxEntry('');
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/configurations/${id}`);
      setConfigurations(configurations.filter(config => config._id !== id));
    } catch (error) {
      console.error('Error deleting configuration:', error);
      alert('An error occurred while trying to delete the configuration.');
    }
  };

  const handleModify = (config) => {
    setSelectedConfig(config._id);
    setStrategyName(config.strategyName);
    setStrategyType(config.strategyType);
    setIndex(config.index);
    setMinEntryTime(config.minEntryTime);
    setMaxEntryTime(config.maxEntryTime);
    setSquareOffTime(config.squareOffTime);
    setQuantity(config.quantity);
    setBookProfit(config.bookProfit);
    setBookLoss(config.bookLoss);
    setTrailingSlGap(config.trailingSlGap);
    setMaxLoss(config.maxLoss);
    setMaxEntry(config.maxEntry);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const data = {
      strategyName: strategyName || `${strategyType} - ${index} - ${new Date().toLocaleString()}`,
      strategyType,
      index,
      minEntryTime,
      maxEntryTime,
      squareOffTime,
      quantity,
      bookProfit,
      bookLoss,
      trailingSlGap,
      maxLoss,
      maxEntry,
    };

    try {
      if (selectedConfig) {
        await axiosInstance.put(`/configurations/${selectedConfig}`, data);
      } else {
        await axiosInstance.post('/configurations', data);
      }
      fetchConfigurations();
      handleAddNew();
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('An error occurred while saving the configuration.');
    }
  };

  const buttonHoverProps = useSpring({ from: { transform: 'scale(1)' }, to: async (next) => {
    while (1) {
      await next({ transform: 'scale(1.05)' });
      await next({ transform: 'scale(1)' });
    }
  }, reset: true });

  return (
    <Container>
      <Title>Strategy Configuration</Title>

      <ConfigList>
  {Array.isArray(configurations) && configurations.map(config => (
    <ConfigItem key={config._id}>
      {/* <input
        type="checkbox"
        checked={selectedConfigIds.includes(config._id)}
        onChange={() => handleCheckboxChange(config._id)}
      /> */}
      <ConfigText>{config.strategyName}</ConfigText>
      <ActionButtons>
        <Button type="button" onClick={() => handleModify(config)}>Modify</Button>
        <DeleteButton type="button" onClick={() => handleDelete(config._id)}>Delete</DeleteButton>
      </ActionButtons>
    </ConfigItem>
  ))}
</ConfigList>

      <Form onSubmit={handleSave}>
        <FormControl>
          <Label>Strategy Name</Label>
          <Input type="text" value={strategyName} onChange={(e) => setStrategyName(e.target.value)} />
        </FormControl>
        <FormControl>
          <Label>Strategy Type</Label>
          <Select value={strategyType} onChange={(e) => setStrategyType(e.target.value)}>
            <option value="">Select a strategy</option>
            <option value="strategy1">Strategy 1</option>
            <option value="strategy2">Strategy 2</option>
          </Select>
        </FormControl>
        <FormControl>
          <Label>Index</Label>
          <Select value={index} onChange={(e) => setIndex(e.target.value)}>
            <option value="">Select an index</option>
            <option value="index1">Index 1</option>
            <option value="index2">Index 2</option>
          </Select>
        </FormControl>
        <FormControl>
          <Label>Minimum Entry Time (09:15 - 15:00)</Label>
          <TimeInput type="time" value={minEntryTime} onChange={(e) => setMinEntryTime(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Maximum Entry Time (After Minimum Entry, Before 15:25)</Label>
          <TimeInput type="time" value={maxEntryTime} onChange={(e) => setMaxEntryTime(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Square Off Time (After Maximum Entry, Before 15:30)</Label>
          <TimeInput type="time" value={squareOffTime} onChange={(e) => setSquareOffTime(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Quantity (1 - 1000)</Label>
          <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Book Profit % (0 - 80)</Label>
          <Input type="number" value={bookProfit} onChange={(e) => setBookProfit(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Book Loss % (0 - 80)</Label>
          <Input type="number" value={bookLoss} onChange={(e) => setBookLoss(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Trailing SL Gap % (Less than Book Loss %)</Label>
          <Input type="number" value={trailingSlGap} onChange={(e) => setTrailingSlGap(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Max Loss Rs.</Label>
          <Input type="number" value={maxLoss} onChange={(e) => setMaxLoss(e.target.value)} required />
        </FormControl>
        <FormControl>
          <Label>Max Entry (1 - 1000)</Label>
          <Input type="number" value={maxEntry} onChange={(e) => setMaxEntry(e.target.value)} required />
        </FormControl>
        <ButtonGroup>
          <Button type="button" onClick={handleAddNew}>Add New</Button>
          <Button type="submit">Save</Button>
        </ButtonGroup>
      </Form>
      

      
{/* <Button 
        style={buttonHoverProps}  
        onClick={handleNextClick} 
        disabled={configurations.length === 0}
      >
        Next
      </Button> */}
      
    </Container>
  );
};

export default StrategyConfiguration;